import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Select, Row, Col, Table } from 'antd';
import NSPDatePicker from '@components/ui/NSPDatepicker';
import { toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';

const { useWatch } = Form;

const EXCLUDED_RATE_TYPES = new Set(['OVERAGE', 'MAX_CAPACITY', 'RETROACTIVE']);

// Same rule as Stat GL / Dollar GL in the General Info section
const glValidationRules = [
  { pattern: /^[0-9.]+$/, message: 'Only numbers and periods are allowed' },
];

const CoreInfoTariffContractInfoSection = ({ form, disabled = false }) => {
  const rateContracts = useWatch('rateContracts', form) || [];

  // Auto-set selectedRateType to CONTRACT — radio selection removed
  useEffect(() => {
    form.setFieldValue('selectedRateType', 'CONTRACT');
  }, [form]);

  const validateEffectiveTo = (index: number) => ({
    validator: (_, value) => {
      const effectiveFrom = form.getFieldValue(['rateContracts', index, 'effectiveFrom']);
      if (effectiveFrom && value && effectiveFrom.isAfter(value)) {
        return Promise.reject(new Error('Must be after Effective From'));
      }
      if (effectiveFrom && !value) {
        return Promise.reject(new Error('Effective To is required'));
      }
      return Promise.resolve();
    },
  });

  const validateEffectiveFrom = (index: number) => ({
    validator: (_, value) => {
      const effectiveTo = form.getFieldValue(['rateContracts', index, 'effectiveTo']);
      if (!value) {
        return Promise.reject(new Error('Effective From is required'));
      }
      if (value && effectiveTo && value.isAfter(effectiveTo)) {
        return Promise.reject(new Error('Must be before Effective To'));
      }
      return Promise.resolve();
    },
  });

  const visibleContracts = (rateContracts || []).filter(
    (rc: { rateType: string }) => !EXCLUDED_RATE_TYPES.has(rc.rateType)
  );

  const allRateContracts: { rateType: string }[] = rateContracts || [];

  const columns = [
    {
      title: 'Rate Type',
      dataIndex: 'rateType',
      render: (value: string) => value,
    },
    {
      title: 'Effective From',
      dataIndex: 'effectiveFrom',
      render: (_, record) => {
        const index = allRateContracts.findIndex((rc) => rc?.rateType === record.rateType);
        return (
          <Form.Item
            name={['rateContracts', index, 'effectiveFrom']}
            data-testid="effective-from-field"
            style={{ margin: 0 }}
            rules={[validateEffectiveFrom(index)]}
          >
            <NSPDatePicker
              picker="month"
              format="YYYY-MM"
              style={{ width: '100%' }}
              disabled={disabled}
              onChange={() => {
                form.validateFields([['rateContracts', index, 'effectiveTo']]);
              }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: 'Effective To',
      dataIndex: 'effectiveTo',
      render: (_, record) => {
        const index = allRateContracts.findIndex((rc) => rc?.rateType === record.rateType);
        return (
          <Form.Item
            name={['rateContracts', index, 'effectiveTo']}
            data-testid="effective-to-field"
            style={{ margin: 0 }}
            rules={[validateEffectiveTo(index)]}
          >
            <NSPDatePicker
              picker="month"
              format="YYYY-MM"
              style={{ width: '100%' }}
              disabled={disabled}
              onChange={() => {
                form.validateFields([['rateContracts', index, 'effectiveFrom']]);
              }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: 'Rate in CAD',
      dataIndex: 'rate',
      render: (_, record) => {
        const index = allRateContracts.findIndex((rc) => rc?.rateType === record.rateType);
        return (
          <Form.Item
            name={['rateContracts', index, 'rate']}
            data-testid="rate-field"
            style={{ margin: 0 }}
            rules={[{ required: true, message: 'Rate is required' }]}
          >
            <Input prefix="$" disabled={disabled} />
          </Form.Item>
        );
      },
    },
  ];

  return (
    <>
      {/* Registers rateContracts and selectedRateType in form state */}
      <Form.Item name="rateContracts" noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="selectedRateType" noStyle>
        <input type="hidden" />
      </Form.Item>

      <Table
        dataSource={visibleContracts}
        columns={withTestColumnAttrs(columns, 'rate-contract-value')}
        pagination={false}
        rowKey="rateType"
        bordered
        style={{ marginBottom: 16 }}
        data-testid="rate-contracts-table"
        data-row-count={String(visibleContracts.length)}
        onRow={(record: { rateType: string }) =>
          ({ 'data-testid': 'rate-contract-row', 'data-rate-type': toTestAttrValue(record.rateType) }) as TestAttrs
        }
      />

      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Form.Item label="Energy Bid for the Year (KWH)" name="energyBid" data-testid="energy-bid-field" rules={[{ required: true, message: 'Energy Bid is required' }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Forecast Fee"
            name="forecastFee"
            data-testid="forecast-fee-field"
            rules={[
              {
                validator: (_, value) => {
                  if (value == null || value === '') return Promise.resolve();
                  const num = Number(value);
                  if (num > 9999.99) {
                    return Promise.reject(new Error('Forecast Fee cannot exceed $9999.99 (####.## format)'));
                  }
                  const str = String(value);
                  const dot = str.indexOf('.');
                  if (dot !== -1 && str.length - dot - 1 > 2) {
                    return Promise.reject(new Error('Forecast Fee cannot have more than 2 decimal places (####.## format)'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              disabled={disabled}
              onChange={() => form.validateFields(['forecastFeeCurrency', ['forecastFeeGl', 'glNumber']])}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Forecast Fee Currency"
            name="forecastFeeCurrency"
            data-testid="forecast-fee-currency-field"
            dependencies={['forecastFee']}
            rules={[
              {
                validator: (_, value) => {
                  const forecastFee = form.getFieldValue('forecastFee');
                  if (forecastFee && Number(forecastFee) !== 0 && !value) {
                    return Promise.reject(new Error('Forecast Fee Currency is required when Forecast Fee is set'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              disabled={disabled}
              placeholder="Select One"
              allowClear
              options={[
                { value: 'CAD', label: 'CAD' },
                { value: 'USD', label: 'USD' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            label="Forecast Fee GL"
            name={['forecastFeeGl', 'glNumber']}
            data-testid="forecast-fee-gl-field"
            dependencies={['forecastFee']}
            rules={[
              ...glValidationRules,
              {
                validator: (_, value) => {
                  const forecastFee = form.getFieldValue('forecastFee');
                  if (forecastFee && Number(forecastFee) !== 0 && !value) {
                    return Promise.reject(new Error('Forecast Fee GL is required when Forecast Fee is set'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Admin Fee Rate"
            name="adminFeeRate"
            data-testid="admin-fee-rate-field"
            rules={[
              {
                validator: (_, value) => {
                  if (value == null || value === '') return Promise.resolve();
                  const num = Number(value);
                  if (num > 99.9999) {
                    return Promise.reject(new Error('Admin Fee Rate cannot exceed $99.9999 (##.#### format)'));
                  }
                  const str = String(value);
                  const dot = str.indexOf('.');
                  if (dot !== -1 && str.length - dot - 1 > 4) {
                    return Promise.reject(new Error('Admin Fee Rate cannot have more than 4 decimal places (##.#### format)'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              prefix="$"
              disabled={disabled}
              onChange={() => form.validateFields([['adminFeeGl', 'glNumber']])}
            />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item
            label="Admin Fee GL"
            name={['adminFeeGl', 'glNumber']}
            data-testid="admin-fee-gl-field"
            dependencies={['adminFeeRate']}
            rules={[
              ...glValidationRules,
              {
                validator: (_, value) => {
                  const adminFeeRate = form.getFieldValue('adminFeeRate');
                  if (adminFeeRate && Number(adminFeeRate) !== 0 && !value) {
                    return Promise.reject(new Error('Admin Fee GL is required when Admin Fee Rate is set'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input disabled={disabled} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default CoreInfoTariffContractInfoSection;
